import { sports, type ClassFill, type Sport } from "./enums.ts";
import type { BusinessLabels } from "./schemas/business.ts";

/**
 * The starting labels for each sport, per the Vocabulary table. Seeded onto a business at creation
 * and by migration 0009's backfill, and shown as a preview on the onboarding sport step so a new
 * owner knows what their business will say before they finish setup. The owner can rename any of
 * them afterwards in Settings.
 */
export function defaultLabels(sport: Sport): BusinessLabels {
  const staff =
    sport === "pilates" || sport === "other"
      ? { singular: "instructor", plural: "instructors" }
      : { singular: "coach", plural: "coaches" };
  const member =
    sport === "swimming" || sport === "football" || sport === "gymnastics"
      ? { singular: "parent", plural: "parents" }
      : sport === "pilates"
        ? { singular: "client", plural: "clients" }
        : { singular: "member", plural: "members" };
  const participant = (() => {
    switch (sport) {
      case "swimming":
        return { singular: "swimmer", plural: "swimmers" };
      case "football":
      case "padel":
        return { singular: "player", plural: "players" };
      case "gymnastics":
        return { singular: "gymnast", plural: "gymnasts" };
      case "pilates":
        return { singular: "client", plural: "clients" };
      default:
        return { singular: "participant", plural: "participants" };
    }
  })();
  const group = (() => {
    switch (sport) {
      case "swimming":
        return { singular: "level", plural: "levels" };
      case "football":
        return { singular: "team", plural: "teams" };
      case "pilates":
        return { singular: "class", plural: "classes" };
      default:
        return { singular: "group", plural: "groups" };
    }
  })();
  return {
    businessSingular: "business",
    businessPlural: "businesses",
    staffSingular: staff.singular,
    staffPlural: staff.plural,
    memberSingular: member.singular,
    memberPlural: member.plural,
    participantSingular: participant.singular,
    participantPlural: participant.plural,
    groupSingular: group.singular,
    groupPlural: group.plural,
  };
}

/**
 * Whether a member may book a participant into any group's session, when the business has not said
 * (the `allowAnyGroup` setting). It follows how classes fill (`class_fill`, migration 0069), never
 * the sport: off for `fixed_group`, where the business places each participant in a group and a
 * member sees and books only the sessions of that group (onboarding version 2, 2026-10-04). On for
 * `pick_class`, where people book any class.
 */
export function defaultAllowAnyGroup(classFill: ClassFill): boolean {
  return classFill === "pick_class";
}

/**
 * "swimmer, client or player": the start words of the sports for one concept, for a hint that
 * shows examples (Settings, Words). The examples come from the table above, so no screen writes a
 * sport word of its own.
 */
export function labelExamples(concept: "staff" | "member" | "participant" | "group", count = 3): string {
  const key = `${concept}Singular` as const;
  const words = [...new Set(sports.map((sport) => defaultLabels(sport)[key]))].slice(0, count);
  return words.length > 1 ? `${words.slice(0, -1).join(", ")} or ${words[words.length - 1]}` : (words[0] ?? "");
}

/**
 * The line under the sport chips on the sign-up (Desk and the phone): "We will say coach, parent,
 * swimmer and level. Change this later in Settings." It shows the words a sport starts with before
 * the business exists to rename them.
 */
export function labelPreviewLine(sport: Sport): string {
  const labels = defaultLabels(sport);
  return `We will say ${labels.staffSingular}, ${labels.memberSingular}, ${labels.participantSingular} and ${labels.groupSingular}. Change this later in Settings.`;
}
